import React from 'react'
import { Link, useLocation } from 'react-router-dom'

// Global site footer — rendered once in App below the routed page. Carries the
// legal/policy links required for payment-gateway review, plus a short brand
// line. Styled to match the site (dark canvas, #e8dcc0 gold accent, Fira Code).
export function Footer() {
  const location = useLocation()
  // The landing page runs a full-screen scroll-locked experience; a footer
  // underneath it would be unreachable/awkward, so skip it there.
  if (location.pathname === '/') return null

  const year = new Date().getFullYear()

  const links = [
    { href: '/about',   label: 'About Us' },
    { href: '/shop',    label: 'Shop' },
    { href: '/contact', label: 'Contact Us' },
    { href: '/terms',   label: 'Terms & Conditions' },
    { href: '/privacy', label: 'Privacy Policy' },
    { href: '/refund',  label: 'Refund Policy' },
  ]

  // A link to the page you're already on is dead weight in the footer, so drop
  // it. Compared against the path with any trailing slash normalised away, so
  // "/shop/" hides the Shop link the same as "/shop" does.
  const currentPath = location.pathname.replace(/\/+$/, '') || '/'
  const visibleLinks = links.filter(l => l.href !== currentPath)

  return (
    <footer style={s.footer}>
      <div style={s.inner}>
        <div style={s.brandCol}>
          <div style={s.brandMark}>
            <span style={s.brandLine}>The</span>
            <span style={s.brandLine}>Perfume</span>
            <span style={s.brandLine}>Culture</span>
          </div>
          <p style={s.tagline}>
            Fragrance Intelligence-crafted custom perfumery, handmade in New Delhi, India.
          </p>
        </div>

        <nav style={s.linkCol} aria-label="Footer">
          {visibleLinks.map(l => (
            <Link key={l.href} to={l.href} style={s.link}>{l.label}</Link>
          ))}
        </nav>
      </div>

      <div style={s.bottom}>
        <span>© {year} The Perfume Culture. All rights reserved.</span>
        <a href="mailto:support@bestowic.com" style={s.email}>
          support@bestowic.com
        </a>
      </div>
    </footer>
  )
}

const s: Record<string, React.CSSProperties> = {
  footer: {
    background: '#0b0806',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    fontFamily: "'Fira Code', monospace",
    padding: '56px 24px 32px',
  },
  inner: {
    maxWidth: '1080px', margin: '0 auto',
    display: 'flex', justifyContent: 'space-between', gap: '40px', flexWrap: 'wrap',
  },
  brandCol: { maxWidth: '320px' },
  brandMark: {
    display: 'flex', flexDirection: 'column',
    color: '#ece2cd', fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.3em', textTransform: 'uppercase',
    fontFamily: "'Rockwell Condensed', 'Fira Code', monospace",
  },
  brandLine: { display: 'block', lineHeight: 1.5 },
  tagline: { margin: '16px 0 0', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 },
  linkCol: {
    display: 'grid', gridTemplateColumns: 'repeat(2, auto)',
    gap: '12px 40px', alignContent: 'start',
  },
  link: {
    fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', textDecoration: 'none',
    letterSpacing: '0.04em', whiteSpace: 'nowrap',
  },
  bottom: {
    maxWidth: '1080px', margin: '40px auto 0', paddingTop: '24px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap',
    fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)',
  },
  email: { color: 'rgba(232,220,192,0.7)', textDecoration: 'none' },
}
